import { useState, useEffect, useRef } from "react";
import { DashboardLayout } from "@/pages/Dashboard";
import { useAuth, useLanguage, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import axios from "axios";
import { Send, Users, MessageCircle, Search } from "lucide-react";

export default function ChatRooms() {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const [rooms, setRooms] = useState([]);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const pollInterval = useRef(null);

  useEffect(() => {
    loadRooms();
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, []);

  useEffect(() => {
    if (selectedRoom) {
      loadMessages();
      pollInterval.current = setInterval(loadMessages, 3000);
    }
    return () => {
      if (pollInterval.current) clearInterval(pollInterval.current);
    };
  }, [selectedRoom]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadRooms = async () => {
    try {
      const response = await axios.get(`${API}/rooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRooms(response.data);
    } catch (error) {
      console.error("Failed to load rooms:", error);
    }
  };

  const loadMessages = async () => {
    if (!selectedRoom) return;
    try {
      const response = await axios.get(`${API}/rooms/${selectedRoom.room_id}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      console.error("Failed to load messages:", error);
    }
  };

  const joinRoom = async (room) => {
    try {
      await axios.post(`${API}/rooms/${room.room_id}/join`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedRoom(room);
      loadRooms();
    } catch (error) {
      toast.error(t("فشل الانضمام للغرفة", "Failed to join room"));
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || !selectedRoom || loading) return;

    const messageContent = input.trim();
    setInput("");
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("content", messageContent);
      
      await axios.post(`${API}/rooms/${selectedRoom.room_id}/messages`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadMessages();
    } catch (error) {
      toast.error(t("فشل إرسال الرسالة", "Failed to send message"));
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      general: "bg-blue-500",
      jobs: "bg-orange-500",
      housing: "bg-green-500",
      state: "bg-purple-500"
    };
    return colors[category] || "bg-slate-500";
  };

  return (
    <DashboardLayout title={t("غرف المحادثة", "Chat Rooms")}>
      <div className="flex gap-6 h-[calc(100vh-200px)]" data-testid="chat-rooms">
        {/* Rooms Sidebar */}
        <div className="w-80 glass-card rounded-xl overflow-hidden flex flex-col">
          <div className="p-4 border-b border-slate-700">
            <h3 className="font-bold text-white mb-2">{t("الغرف", "Rooms")}</h3>
          </div>
          
          <ScrollArea className="flex-1">
            <div className="p-2 space-y-1">
              {rooms.map((room) => (
                <button
                  key={room.room_id}
                  onClick={() => joinRoom(room)}
                  className={`w-full text-right p-4 rounded-xl transition-colors ${
                    selectedRoom?.room_id === room.room_id 
                      ? 'bg-yellow-500/10 border-r-4 border-yellow-500' 
                      : 'hover:bg-slate-800'
                  }`}
                  data-testid={`room-${room.room_id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-12 h-12 ${getCategoryColor(room.category)} rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}>
                      {room.name.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-white truncate">{room.name}</p>
                      <p className="text-sm text-slate-400 truncate">{room.description}</p>
                      <div className="flex items-center gap-1 mt-1 text-xs text-slate-500">
                        <Users size={12} />
                        <span>{room.members_count} {t("عضو", "members")}</span>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* Chat Area */}
        <div className="flex-1 glass-card rounded-xl flex flex-col overflow-hidden">
          {selectedRoom ? (
            <>
              {/* Room Header */}
              <div className="p-4 border-b border-slate-700 bg-slate-800/50">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 ${getCategoryColor(selectedRoom.category)} rounded-full flex items-center justify-center text-white font-bold text-lg`}>
                    {selectedRoom.name.charAt(0)}
                  </div>
                  <div>
                    <h3 className="font-bold text-white">{selectedRoom.name}</h3>
                    <p className="text-sm text-slate-400">{selectedRoom.members_count} {t("عضو", "members")}</p>
                  </div>
                </div>
              </div>

              {/* Messages */}
              <ScrollArea className="flex-1 p-4">
                <div className="space-y-4">
                  {messages.length === 0 ? (
                    <div className="text-center py-12">
                      <MessageCircle className="mx-auto text-slate-600 mb-4" size={48} />
                      <p className="text-slate-400">{t("لا توجد رسائل. كن أول من يرسل!", "No messages. Be the first!")}</p>
                    </div>
                  ) : (
                    messages.map((msg) => {
                      const isOwn = msg.user_id === user?.user_id;
                      return (
                        <div
                          key={msg.message_id}
                          className={`flex gap-3 ${isOwn ? 'flex-row-reverse' : ''}`}
                        >
                          <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center text-black font-bold flex-shrink-0">
                            {msg.user_name?.charAt(0) || "م"}
                          </div>
                          <div className={`max-w-[70%] ${isOwn ? 'text-left' : 'text-right'}`}>
                            <p className={`text-xs text-slate-500 mb-1 ${isOwn ? 'text-left' : 'text-right'}`}>
                              {msg.user_name}
                            </p>
                            <div className={`p-3 rounded-2xl ${
                              isOwn 
                                ? 'chat-bubble-sent' 
                                : 'bg-slate-800 text-slate-200 rounded-bl-sm'
                            }`}>
                              <p>{msg.content}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>
              </ScrollArea>

              {/* Input Area */}
              <div className="p-4 border-t border-slate-700">
                <div className="flex gap-3">
                  <Input
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={(e) => e.key === "Enter" && sendMessage()}
                    placeholder={t("اكتب رسالتك...", "Type your message...")}
                    className="flex-1 h-12 bg-slate-900 border-slate-700 text-white"
                    disabled={loading}
                    data-testid="chat-input"
                  />
                  <Button
                    onClick={sendMessage}
                    disabled={!input.trim() || loading}
                    className="btn-primary px-6"
                    data-testid="chat-send-btn"
                  >
                    <Send size={20} />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-20 h-20 bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="text-slate-600" size={40} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{t("اختر غرفة للمحادثة", "Select a room")}</h3>
                <p className="text-slate-400">{t("اختر غرفة من القائمة للانضمام", "Choose from the list to join")}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
